import { supabase } from '../lib/supabase';
import { useToast } from './useToast';
import { useConfirm } from './useConfirm';
import { useTimesheetEdit } from './useTimesheetEdit';
import {
  resolveWeeklyHours,
  calculateWeeklyOvertime,
  getWeekStartDate,
} from '../utils/overtimeUtils';
import { generateWeekDays } from '../utils/dateUtils';
import { getFrenchHolidays } from '../utils/holidayUtils';
import type { TimeEntryDetail, PendingSheet } from './useValidateData';
import { formatMonthFr } from '../utils/sheetStatusUtils';
import { formatDiversEntryLabel } from '../utils/diversDisplayUtils';
import {
  buildTimesheetPDF,
  optimizeImage,
  LOGO_URL,
  SIGN_URL,
  type PDFProjectRow,
  type PDFIndemnityStats,
  type PDFGDStats,
  type PDFHoursStats,
} from '../utils/timesheetPDFBuilder';

interface UseValidateActionsParams {
  selectedSheetId: string | null;
  setSelectedSheetId: (id: string | null) => void;
  currentSheet: PendingSheet | undefined;
  entries: TimeEntryDetail[];
  setEntries: React.Dispatch<React.SetStateAction<TimeEntryDetail[]>>;
  pendingSheets: PendingSheet[];
  refreshSheets: () => Promise<void> | void;
  refreshInProgress?: () => Promise<void> | void;
  loadSheetDetails: (sheetId: string) => Promise<void>;
  setOverrideLoading: (v: boolean) => void;
  setZoneSavedEntryId: (v: string | null) => void;
  setSavedFieldId: (v: string | null) => void;
  normalHoursTotal: number;
  saturdayHoursTotal: number;
  sundayHoursTotal: number;
  hasGD: boolean;
}

export function useValidateActions({
  selectedSheetId,
  setSelectedSheetId,
  currentSheet,
  entries,
  setEntries,
  refreshSheets,
  refreshInProgress,
  loadSheetDetails,
  setOverrideLoading,
  setZoneSavedEntryId,
  setSavedFieldId,
  normalHoursTotal,
  saturdayHoursTotal,
  sundayHoursTotal,
  hasGD,
}: UseValidateActionsParams) {
  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();
  const { editTimeEntry, editWeeklySheet, validateSheet, rejectSheet } = useTimesheetEdit();

  // Après une action qui change le status, on refresh les deux listes :
  // - pendingSheets (À valider) : sheet peut en sortir (validated, draft) ou y entrer
  // - inProgressSheets (En cours) : sheet peut y entrer (cheval partielle validée) ou en sortir
  const fetchData = async () => {
    await Promise.all([
      Promise.resolve(refreshSheets()),
      refreshInProgress ? Promise.resolve(refreshInProgress()) : Promise.resolve(),
    ]);
  };

  /**
   * Valide tous les jours soumis de la feuille. Utilisé pour les semaines
   * mono-mois ou pour "Valider tout" sur une semaine à cheval dont les
   * deux mois ont été soumis.
   */
  const handleValidate = async () => {
    if (!selectedSheetId) return;
    const ok = await confirmDialog({
      title: "Valider cette feuille ?",
      message: "Tous les jours soumis seront marqués comme validés.",
      variant: "info",
      confirmLabel: "Valider"
    });
    if (!ok) return;
    const success = await validateSheet(selectedSheetId, 'admin_validate');
    if (success) {
      setSelectedSheetId(null);
      await fetchData();
    }
  };

  /**
   * Valide uniquement les jours d'un mois donné (ex: '2026-03').
   * Sur une semaine à cheval, permet de clôturer mars sans attendre avril,
   * tout en laissant la sheet éditable pour avril (status reste 'pending').
   */
  const handleValidateMonth = async (monthKey: string) => {
    if (!selectedSheetId) return;
    const monthLabel = formatMonthFr(monthKey);
    const ok = await confirmDialog({
      title: `Valider les heures de ${monthLabel} ?`,
      message: `Seuls les jours de ${monthLabel} seront validés. La feuille reste ouverte pour les autres mois.`,
      variant: "info",
      confirmLabel: "Valider"
    });
    if (!ok) return;
    const success = await validateSheet(selectedSheetId, 'admin_validate', monthKey);
    if (success) {
      // Rester sur la feuille si elle n'est pas complètement validée
      // (cheval avec avril encore en cours) — refresh pour voir l'état à jour
      await fetchData();
    }
  };

  const handleReject = async () => {
    if (!selectedSheetId) return;
    const reason = await confirmDialog({
      title: "Motif du rejet",
      input: true,
      variant: "warning",
      inputPlaceholder: "Saisissez le motif du rejet..."
    });
    if (!reason) return;
    const success = await rejectSheet(selectedSheetId, reason as string, 'admin_validate');
    if (success) {
      setSelectedSheetId(null);
      await fetchData();
    }
  };

  // Handler override base horaire hebdo
  const handleOverrideChange = async (value: string) => {
    if (!selectedSheetId || !currentSheet) return;
    setOverrideLoading(true);
    try {
      const newOverride = value === '' ? null : parseInt(value);

      const effectiveHours = resolveWeeklyHours(newOverride, currentSheet.employee?.custom_weekly_hours);
      const hoursLuSa = normalHoursTotal + saturdayHoursTotal;
      const isBE35h = currentSheet.employee?.role === 'bureau_etude';
      const breakdown = calculateWeeklyOvertime(currentSheet.week_number, hoursLuSa, sundayHoursTotal, hasGD || !!isBE35h, effectiveHours);

      const success = await editWeeklySheet(
        selectedSheetId,
        {
          override_weekly_hours: newOverride,
          overtime_25: parseFloat(breakdown.overtime25.toFixed(2)),
          overtime_50: parseFloat(breakdown.overtime50.toFixed(2)),
          regular_hours: parseFloat(breakdown.regularHours.toFixed(2)),
          sunday_hours: parseFloat(breakdown.sundayHours.toFixed(2)),
        },
        { actorPage: 'admin_validate' }
      );

      if (success) {
        await refreshSheets();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setOverrideLoading(false);
    }
  };

  // --- MODIFICATION ZONE À LA VOLÉE ---
  const handleZoneChange = async (entryId: string, newZone: string) => {
    const targetEntry = entries.find(e => e.id === entryId);
    if (!targetEntry) return;

    const workDate = targetEntry.work_date;

    const sameDayEntryIds = entries
      .filter(e => e.work_date === workDate)
      .map(e => e.id);

    const isGD = newZone === 'GD';
    const updatePayload: Record<string, unknown> = {
      travel_zone: newZone === '' ? null : newZone,
    };

    if (isGD) {
      updatePayload.is_grand_deplacement = true;
      updatePayload.indemnity_transport = 0;
      updatePayload.indemnity_trajet = 0;
    } else if (targetEntry.is_grand_deplacement === true) {
      updatePayload.is_grand_deplacement = false;
      updatePayload.gd_amount = null;
      updatePayload.gd_travel_hours = null;
    }

    setEntries(prev => prev.map(e => {
      if (!sameDayEntryIds.includes(e.id)) return e;
      return {
        ...e,
        travel_zone: newZone === '' ? null : newZone,
        is_grand_deplacement: isGD ? true : (e.is_grand_deplacement === true ? false : e.is_grand_deplacement),
        ...(isGD ? { indemnity_transport: 0, indemnity_trajet: 0 } : {}),
        ...(!isGD && e.is_grand_deplacement === true ? { gd_amount: null, gd_travel_hours: null } : {}),
      } as TimeEntryDetail;
    }));

    setZoneSavedEntryId(entryId);
    setTimeout(() => setZoneSavedEntryId(null), 1500);

    try {
      if (!selectedSheetId) return;
      const results = await Promise.all(
        sameDayEntryIds.map(id =>
          editTimeEntry(id, updatePayload, {
            sheetId: selectedSheetId,
            actorPage: 'admin_validate',
          })
        )
      );

      if (results.some(ok => !ok)) {
        if (selectedSheetId) loadSheetDetails(selectedSheetId);
      }
    } catch (err) {
      console.error(err);
      if (selectedSheetId) loadSheetDetails(selectedSheetId);
    }
  };

  // --- APPLIQUER UNE ZONE À TOUTES LES ENTRÉES ---
  const handleApplyZoneToAll = async (newZone: string) => {
    if (entries.length === 0) return;

    const allEntryIds = entries.map(e => e.id);
    const isGD = newZone === 'GD';

    const updatePayload: Record<string, unknown> = {
      travel_zone: newZone === '' ? null : newZone,
    };

    if (isGD) {
      updatePayload.is_grand_deplacement = true;
      updatePayload.indemnity_transport = 0;
      updatePayload.indemnity_trajet = 0;
    } else {
      updatePayload.is_grand_deplacement = false;
      updatePayload.gd_amount = null;
      updatePayload.gd_travel_hours = null;
    }

    setEntries(prev => prev.map(e => ({
      ...e,
      travel_zone: newZone === '' ? null : newZone,
      is_grand_deplacement: isGD,
      ...(isGD ? { indemnity_transport: 0, indemnity_trajet: 0 } : {}),
      ...(!isGD ? { gd_amount: null, gd_travel_hours: null } : {}),
    } as TimeEntryDetail)));

    setZoneSavedEntryId('__all__');
    setTimeout(() => setZoneSavedEntryId(null), 1500);

    try {
      if (!selectedSheetId) return;
      const results = await Promise.all(
        allEntryIds.map(id =>
          editTimeEntry(id, updatePayload, {
            sheetId: selectedSheetId,
            actorPage: 'admin_validate',
          })
        )
      );

      if (results.some(ok => !ok)) {
        if (selectedSheetId) loadSheetDetails(selectedSheetId);
      }
    } catch (err) {
      console.error(err);
      if (selectedSheetId) loadSheetDetails(selectedSheetId);
    }
  };

  // --- MODIFICATION PANIERS / TRANSPORT / TRAJET À LA VOLÉE ---
  const handleIndemnityToggle = async (
    entryId: string,
    field: 'meals_count' | 'indemnity_transport' | 'indemnity_trajet',
    newValue: number
  ) => {
    const targetEntry = entries.find(e => e.id === entryId);
    if (!targetEntry) return;

    const sameDayEntryIds = entries
      .filter(e => e.work_date === targetEntry.work_date)
      .map(e => e.id);

    setEntries(prev => prev.map(e => {
      if (!sameDayEntryIds.includes(e.id)) return e;
      return { ...e, [field]: newValue } as TimeEntryDetail;
    }));

    setSavedFieldId(`${entryId}-${field}`);
    setTimeout(() => setSavedFieldId(null), 1200);

    try {
      if (!selectedSheetId) return;
      const results = await Promise.all(
        sameDayEntryIds.map(id =>
          editTimeEntry(id, { [field]: newValue }, {
            sheetId: selectedSheetId,
            actorPage: 'admin_validate',
          })
        )
      );

      if (results.some(ok => !ok)) {
        if (selectedSheetId) loadSheetDetails(selectedSheetId);
      }
    } catch (err) {
      console.error(err);
      if (selectedSheetId) loadSheetDetails(selectedSheetId);
    }
  };

  // --- RÉGÉNÉRATION PDF ---
  const regeneratePdf = async () => {
    // Utilise currentSheet (qui cherche dans les 2 listes : pendingSheets + inProgressSheets)
    // plutôt que pendingSheets uniquement — sinon les feuilles en cours (cheval partielle
    // validée) ne pouvaient pas régénérer leur PDF.
    const sheet = currentSheet;
    if (!sheet) {
      toast.error('Feuille introuvable.');
      return;
    }
    if (entries.length === 0) {
      toast.warning('Aucune saisie : le PDF ne peut pas être généré.');
      return;
    }

    setOverrideLoading(true);
    try {
      // 1. Charger images (best-effort)
      let logoData: string | null = null;
      let directionSignData: string | null = null;
      try {
        const [logo, direction] = await Promise.all([
          optimizeImage(LOGO_URL, 400),
          optimizeImage(SIGN_URL, 300)
        ]);
        logoData = logo || null;
        directionSignData = direction || null;
      } catch { /* images optionnelles */ }

      // 2. Calculer les jours de la semaine
      const weekStart = getWeekStartDate(sheet.year, sheet.week_number);
      const weekDays = generateWeekDays(weekStart);
      const holidays = getFrenchHolidays(sheet.year);
      const empName = sheet.employee?.full_name || 'Inconnu';
      const weekNum = sheet.week_number;
      const year = sheet.year;

      // 3. Grouper les entries par projet → PDFProjectRow[]
      const projectMap = new Map<string, { dayHours: Record<string, number> }>();
      const trainingDates: string[] = [];

      entries.forEach(e => {
        const code = e.project?.code || '???';
        const name = e.project?.name || 'Inconnu';
        // Priorité : si divers_sub_project est rejoint (nouvelle colonne mai 2026),
        // utiliser son name (source structurée fiable). Sinon fallback sur la
        // description legacy. Demande J.-L. OGER : format `26E0259 - FIMOR`.
        const key = e.divers_sub_project
          ? `${code} - ${e.divers_sub_project.name}`
          : (e.project?.category === 'divers' && e.description
              ? formatDiversEntryLabel(code, e.description)
              : `${code} - ${name}`);
        const totalH = (e.hours_normal || 0) + (e.hours_saturday || 0) + (e.hours_major || 0);

        if (code === 'SYS-FORM') {
          if (!trainingDates.includes(e.work_date)) trainingDates.push(e.work_date);
          return;
        }

        if (!projectMap.has(key)) {
          projectMap.set(key, { dayHours: {} });
        }
        const proj = projectMap.get(key)!;
        proj.dayHours[e.work_date] = (proj.dayHours[e.work_date] || 0) + totalH;
      });

      const projectRows: PDFProjectRow[] = [];
      projectMap.forEach((proj, key) => {
        projectRows.push({ projectName: key, dayHours: proj.dayHours });
      });

      // 4. Agréger les stats depuis les entries
      let totalMeals = 0, totalTransport = 0, totalTrajet = 0;
      const zonesCount: Record<string, number> = {};
      let totalNormal = trainingDates.length * 7;
      let totalSaturday = 0, totalSunday = 0;
      let totalGDDays = 0, totalGDAmount = 0, totalGDTravelHours = 0;
      const processedDays = new Set<string>();
      const processedGDDays = new Set<string>();
      const gdProjectsForAmplitude = new Set<string>();
      let gdProjectName = '';

      entries.forEach(e => {
        if (e.project?.code === 'SYS-FORM') return;

        totalNormal += (e.hours_normal || 0);
        totalSaturday += (e.hours_saturday || 0);
        totalSunday += (e.hours_major || 0);

        if (!processedDays.has(e.work_date)) {
          if (!e.is_grand_deplacement) {
            totalMeals += (e.meals_count || 0);
            totalTransport += (e.indemnity_transport || 0);
            totalTrajet += (e.indemnity_trajet || 0);
          }
          if (!e.is_grand_deplacement && e.travel_zone &&
              e.travel_zone.toString().toUpperCase() !== 'GD') {
            const zStr = e.travel_zone.toString();
            zonesCount[zStr] = (zonesCount[zStr] || 0) + 1;
          }
          processedDays.add(e.work_date);
        }

        if (e.is_grand_deplacement && !processedGDDays.has(e.work_date)) {
          totalGDDays++;
          if (e.gd_amount) totalGDAmount += e.gd_amount;
          if (!gdProjectName && e.project) {
            gdProjectName = `${e.project.code} - ${e.project.name}`;
          }
          const projKey = e.project?.code || '';
          if (e.gd_travel_hours && !gdProjectsForAmplitude.has(projKey)) {
            totalGDTravelHours += e.gd_travel_hours;
            gdProjectsForAmplitude.add(projKey);
          }
          processedGDDays.add(e.work_date);
        }
      });

      const gdDailyRate = totalGDDays > 0 && totalGDAmount > 0 ? totalGDAmount / totalGDDays : 0;
      const pdfIsBE35h = sheet.employee?.role === 'bureau_etude';

      const indemnityStats: PDFIndemnityStats = { totalMeals, totalTransport, totalTrajet, zonesCount };
      const gdStats: PDFGDStats = { totalGDDays, totalGDAmount, totalGDTravelHours, gdProjectName: gdProjectName || 'Projet GD', gdDailyRate };
      const hoursStats: PDFHoursStats = { totalNormal, totalSaturday, totalSunday };

      // 5. Construire le PDF via le builder partagé
      const doc = buildTimesheetPDF({
        projectRows, weekDays, holidays, trainingDays: trainingDates,
        indemnityStats, gdStats, hoursStats,
        employeeName: empName,
        weekNumber: weekNum, year,
        dateLabel: `Régénéré le : ${new Date().toLocaleDateString('fr-FR')}`,
        isBE35h: !!pdfIsBE35h,
        customWeeklyHours: sheet.employee?.custom_weekly_hours || null,
        overrideWeeklyHours: sheet.override_weekly_hours || null,
        employeeSignature: sheet.signature_url || null,
        logoData, directionSignData,
      });

      // 6. Supprimer l'ancien PDF (best-effort)
      if (sheet.pdf_url) {
        try {
          const oldUrl = new URL(sheet.pdf_url);
          const pathParts = oldUrl.pathname.split('/');
          const oldFileName = pathParts[pathParts.length - 1];
          if (oldFileName) {
            await supabase.storage.from('timesheets').remove([oldFileName]);
          }
        } catch { /* ignorer erreurs de nettoyage */ }
      }

      // 7. Upload du nouveau PDF
      const pdfBlob = doc.output('blob');
      const safeName = empName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `${year}_S${weekNum}_${safeName}_${Date.now()}.pdf`;

      const { error: uploadError } = await supabase.storage
        .from('timesheets')
        .upload(fileName, pdfBlob, { contentType: 'application/pdf', upsert: true });

      if (uploadError) {
        console.error('Erreur upload PDF:', uploadError);
        toast.error('Erreur lors de l\'upload du PDF régénéré.');
        return;
      }

      const { data: urlData } = supabase.storage
        .from('timesheets')
        .getPublicUrl(fileName);
      const newPdfUrl = urlData.publicUrl;

      // 8. Mise à jour en DB
      const { error: updateError } = await supabase.from('weekly_sheets')
        .update({ pdf_url: newPdfUrl })
        .eq('id', sheet.id);

      if (updateError) {
        console.error('Erreur update pdf_url:', updateError);
        toast.error('PDF uploadé mais erreur lors de la mise à jour du lien.');
        return;
      }

      // Rafraîchit les DEUX listes : la feuille peut être dans pendingSheets
      // (À valider) ou inProgressSheets (En cours). Sans ça, le currentSheet
      // garde l'ancien pdf_url → clic Télécharger = 404 (le fichier vient d'être supprimé).
      await fetchData();
      toast.success('PDF régénéré avec succès');

    } catch (err) {
      console.error('Erreur régénération PDF:', err);
      toast.error('Erreur lors de la régénération du PDF.');
    } finally {
      setOverrideLoading(false);
    }
  };

  return {
    handleValidate,
    handleValidateMonth,
    handleReject,
    handleOverrideChange,
    handleZoneChange,
    handleApplyZoneToAll,
    handleIndemnityToggle,
    regeneratePdf,
  };
}
